import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, ShieldCheck, Sparkles, Loader2 } from "lucide-react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { RelatedLinks } from "@/components/RelatedLinks";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useCampaign } from "@/hooks/useCampaign";
import { SITE } from "@/data/site";

const JOTFORM_URL = process.env.REACT_APP_JOTFORM_ADULT_URL || "";

export default function AdultRegistrationPage() {
  const [loaded, setLoaded] = useState(false);
  useCampaign();

  usePageMeta(
    "USDD · Adult Registration",
    "Register for USDD Academy adult classes, weekly training and courses across London & Kent. Hip-Hop, Afro, Dancehall, Krump, Amapiano, freestyle and performance development."
  );

  return (
    <>
      <Header />
      <main>
        <PageHero
          overline="Adult Registration"
          title="Sign up."
          highlight="Train weekly."
          description="Register for USDD Academy adult classes, courses and weekly training. Hip-Hop, Afro Dance, Dancehall, Krump, Amapiano, Freestyle & Battle Training and Performance Development — across London & Kent."
          ctaPrimary={{ label: "Browse pricing", to: "/pricing" }}
          ctaSecondary={{ label: "See timetable", to: "/schedule" }}
          bg={SITE.classImg1}
        />

        <section data-focus="adult" className="py-16 sm:py-20 bg-black">
          <div className="max-w-5xl mx-auto px-6 sm:px-8">
            <div className="flex items-center gap-3 mb-6">
              <Sparkles size={16} className="text-brand-yellow" />
              <span className="text-[10px] font-bold uppercase tracking-[0.3em] text-brand-yellow">
                Registration form
              </span>
            </div>

            {JOTFORM_URL ? (
              <div className="relative rounded-3xl border border-white/10 overflow-hidden bg-white">
                {!loaded && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black/80 z-10">
                    <Loader2 className="text-brand-yellow animate-spin" size={28} />
                  </div>
                )}
                <iframe
                  data-testid="adult-jotform-iframe"
                  src={JOTFORM_URL}
                  title="USDD adult registration form"
                  width="100%"
                  height="720"
                  frameBorder="0"
                  onLoad={() => setLoaded(true)}
                  className="w-full"
                  style={{ minHeight: "720px", background: "white" }}
                />
                <div className="absolute top-3 right-3 z-20">
                  <a
                    href={JOTFORM_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur border border-white/15 text-[10px] uppercase tracking-[0.25em] text-zinc-300 hover:text-brand-yellow"
                  >
                    Open form <ExternalLink size={11} />
                  </a>
                </div>
              </div>
            ) : (
              <div
                data-testid="adult-jotform-placeholder"
                className="relative rounded-3xl border border-brand-yellow/30 bg-gradient-to-br from-brand-purpleDeep/40 to-zinc-950 p-8 sm:p-12 overflow-hidden"
              >
                <div className="absolute -top-12 -right-12 w-56 h-56 rounded-full bg-brand-yellow/10 blur-3xl" />
                <div className="text-[10px] uppercase tracking-[0.3em] text-brand-yellow mb-2">
                  Registration form loading soon
                </div>
                <h2 className="font-display text-3xl sm:text-4xl text-white tracking-wide leading-tight">
                  We're wiring up the form.
                </h2>
                <p className="mt-3 text-sm text-zinc-400 max-w-xl">
                  In the meantime, drop us a message and we'll register you
                  manually within 24 hours.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  <Link
                    to="/contact"
                    data-testid="adult-registration-contact-btn"
                    className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-brand-yellow text-black font-black uppercase text-xs tracking-[0.2em] hover:bg-brand-yellowHover transition-all"
                  >
                    Register via message <ArrowRight size={14} />
                  </Link>
                </div>
              </div>
            )}

            <div className="mt-6 flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.25em] text-zinc-500">
              <ShieldCheck size={12} /> Your data is kept private. We never share registrations.
            </div>
          </div>
        </section>

        <RelatedLinks
          title="While you're here"
          items={[
            { tag: "Training", label: "Weekly Training", desc: "All adult classes & disciplines", to: "/training" },
            { tag: "Pricing", label: "Pricing & checkout", desc: "Drop-ins, terms and packages", to: "/pricing" },
            { tag: "Schedule", label: "Live timetable", desc: "Book a class via Acuity", to: "/schedule" },
          ]}
        />
      </main>
      <Footer />
    </>
  );
}
